import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Clock, CreditCard, Loader2, Sparkles, Zap } from 'lucide-react';
import type { GymPayment, SubscriptionPlan } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, EmptyState, Skeleton, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { useAsync } from '../lib/useAsync';
import { formatDate, money } from '../lib/format';
import { isFreeTrial } from '../lib/subscription';
import {
  getPublicPricing,
  getSubscription,
  getSubscriptionPaymentStatus,
  getSubscriptionPayments,
  startSubscriptionCheckout,
} from '../lib/gymApi';

type Cycle = 'MONTHLY' | 'ANNUAL';

const STATUS_LABEL: Record<string, string> = {
  TRIAL: 'Free trial',
  ACTIVE: 'Active',
  PAST_DUE: 'Past due',
  CANCELED: 'Canceled',
};

const METHOD_LABEL: Record<string, string> = {
  CASH: 'Cash',
  ECOCASH: 'EcoCash',
  BANK_TRANSFER: 'Bank transfer',
  ONLINE: 'Online',
  OTHER: 'Other',
};

/** Labels for the enforceable plan capabilities (mirrors the backend PlanFeature catalog). */
const ENTITLEMENT_LABEL: Record<string, string> = {
  AUTOMATED_MESSAGING: 'Automated client WhatsApp/SMS',
  MEMBER_REMINDERS: 'Membership expiry reminders',
  ADVANCED_REPORTS: 'Advanced reports & exports',
  CUSTOM_BRANDING: 'Custom branding',
};

export function BillingPage() {
  const { data: sub, reload: reloadSub } = useAsync(getSubscription);
  const { data: pricing } = useAsync(getPublicPricing);
  const { data: history, loading: historyLoading, reload: reloadHistory } = useAsync(getSubscriptionPayments);
  const [cycle, setCycle] = useState<Cycle>('MONTHLY');
  const [params, setParams] = useSearchParams();
  const returnedRef = params.get('ref');

  return (
    <section className="animate-fade-up">
      <PageHeader
        title="Billing"
        description="Choose or renew your TechSoFit plan. Past payments sit below."
      />

      {sub && isFreeTrial(sub) && sub.trialDaysLeft != null && (
        <div className={cn('mb-5 flex items-center gap-3 rounded-card px-4 py-3',
          sub.trialDaysLeft > 0 ? 'bg-dark text-on-dark' : 'bg-bad-soft text-bad-ink')}>
          <Clock className="size-5 shrink-0" aria-hidden />
          <p className="flex-1 text-sm font-medium">
            {sub.trialDaysLeft > 0
              ? `${sub.trialDaysLeft} ${sub.trialDaysLeft === 1 ? 'day' : 'days'} left in your free trial. Choose a plan to keep premium features after it ends.`
              : 'Your free trial has ended. Choose a plan below to re-enable premium features.'}
          </p>
        </div>
      )}

      {returnedRef && (
        <PaymentResult
          reference={returnedRef}
          onResolved={() => {
            reloadSub();
            reloadHistory();
          }}
          onDismiss={() => {
            params.delete('ref');
            setParams(params, { replace: true });
          }}
        />
      )}

      {sub && (() => {
        const freeTrack = isFreeTrial(sub);
        let detail: ReactNode;
        if (sub.locked) {
          detail = 'Access paused — renew to restore';
        } else if (sub.status === 'PAST_DUE' && sub.graceDaysLeft != null) {
          const n = sub.graceDaysLeft;
          detail = `Access ends ${n <= 0 ? 'today' : n === 1 ? 'tomorrow' : `in ${n} days`}`;
        } else if (freeTrack) {
          detail = sub.trialEndsAt ? `Trial ends ${sub.trialEndsAt}` : 'Free trial';
        } else {
          const cap = sub.status === 'ACTIVE' ? sub.effectiveMemberLimit : null;
          detail = (
            <>
              {sub.memberCount} {sub.memberCount === 1 ? 'member' : 'members'}{cap != null ? ` of ${cap}` : ''}
              {sub.currentPeriodEnd && <> · renews {sub.currentPeriodEnd}</>}
            </>
          );
        }
        return (
          <Card className="mb-5 flex flex-wrap items-center gap-x-6 gap-y-2 p-5">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted">
                {freeTrack ? 'Your trial' : 'Current plan'}
              </p>
              <p className="font-display text-xl text-ink">{freeTrack ? 'Free trial' : sub.planName}</p>
            </div>
            <span className={cn('rounded-pill px-2.5 py-0.5 text-xs font-semibold',
              sub.status === 'ACTIVE' ? 'bg-ok-soft text-ok-ink' : sub.status === 'PAST_DUE' ? 'bg-bad-soft text-bad-ink' : 'bg-surface text-ink-soft')}>
              {STATUS_LABEL[sub.status] ?? sub.status}
            </span>
            <div className="text-sm text-muted">{detail}</div>
          </Card>
        );
      })()}

      {pricing && pricing.annualDiscountPercent > 0 && (
        <div className="mb-4 inline-flex rounded-pill bg-surface p-1 text-sm">
          {(['MONTHLY', 'ANNUAL'] as Cycle[]).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCycle(c)}
              className={cn('rounded-pill px-4 py-1.5 font-medium transition-colors', cycle === c ? 'bg-card text-ink shadow-card' : 'text-muted')}
            >
              {c === 'MONTHLY' ? 'Monthly' : `Annual · save ${pricing.annualDiscountPercent}%`}
            </button>
          ))}
        </div>
      )}

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(pricing?.plans ?? []).filter((p) => p.active).map((p) => (
          <PlanCard key={p.id} plan={p} cycle={cycle} currentPlanName={sub?.planName} />
        ))}
      </div>

      <Card className="overflow-hidden p-0">
        <div className="border-b border-line px-5 py-4">
          <h2 className="font-display text-lg font-semibold text-ink">Payment history</h2>
          <p className="text-sm text-muted">Past TechSoFit subscription payments for this gym.</p>
        </div>
        {historyLoading && !history ? (
          <div className="space-y-2 p-5">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : (history ?? []).length === 0 ? (
          <EmptyState
            title="No payments yet"
            description="When you pay for a plan, the invoice lands here."
          />
        ) : (
          <ul className="divide-y divide-line">
            {(history ?? []).map((p) => (
              <PaymentRow key={p.id} payment={p} />
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}

function PaymentRow({ payment }: { payment: GymPayment }) {
  const cycle = payment.billingCycle === 'ANNUAL' ? 'Annual' : 'Monthly';
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">
          {payment.planName} · {cycle}
        </p>
        <p className="text-xs text-muted">
          Paid {formatDate(payment.paidOn)}
          {payment.periodEnd ? ` · covered until ${formatDate(payment.periodEnd)}` : ''}
          {payment.method ? ` · ${METHOD_LABEL[payment.method] ?? payment.method}` : ''}
        </p>
        {payment.note && <p className="mt-0.5 text-xs text-muted">{payment.note}</p>}
      </div>
      <p className="tabular text-sm font-semibold text-ink">
        {money(payment.amount)}
        {payment.currency && payment.currency !== 'USD' ? ` ${payment.currency}` : ''}
      </p>
    </li>
  );
}

function PlanCard({ plan, cycle, currentPlanName }: { plan: SubscriptionPlan; cycle: Cycle; currentPlanName?: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const free = Number(plan.monthlyPrice) <= 0;
  const price = cycle === 'ANNUAL' ? plan.annualPrice : plan.monthlyPrice;
  const isCurrent = currentPlanName === plan.name;

  async function pay() {
    setBusy(true);
    setError(null);
    try {
      const { redirectUrl } = await startSubscriptionCheckout(plan.id, cycle);
      window.location.assign(redirectUrl);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not start the payment.');
      setBusy(false);
    }
  }

  return (
    <Card className={cn('flex flex-col gap-3 p-5', plan.popular && 'ring-1 ring-accent-strong')}>
      <div className="flex items-center justify-between">
        <p className="font-display text-lg font-semibold text-ink">{plan.name}</p>
        {plan.popular && <span className="inline-flex items-center gap-1 rounded-pill bg-accent px-2 py-0.5 text-xs font-semibold text-accent-ink"><Sparkles className="size-3" aria-hidden /> Popular</span>}
      </div>
      {plan.tagline && <p className="text-sm text-muted">{plan.tagline}</p>}
      <p className="text-ink">
        <span className="font-display text-2xl">{plan.currency} {price}</span>
        <span className="text-sm text-muted"> / {cycle === 'ANNUAL' ? 'year' : 'month'}</span>
      </p>
      <p className="text-sm text-muted">
        {plan.memberCap == null ? 'Unlimited members' : `Up to ${plan.memberCap} members`}
        {' · '}
        {plan.staffCap == null ? 'unlimited staff' : `up to ${plan.staffCap} staff`}
      </p>
      {plan.features.length > 0 && (
        <ul className="flex flex-col gap-1.5 text-sm text-ink-soft">
          {plan.features.map((f) => (
            <li key={f} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-accent-strong" aria-hidden /> {f}</li>
          ))}
        </ul>
      )}
      {plan.entitlements.length > 0 && (
        <div className="rounded-control bg-surface p-3">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Unlocks</p>
          <ul className="flex flex-col gap-1 text-sm text-ink">
            {plan.entitlements.map((e) => (
              <li key={e} className="flex gap-2"><Zap className="mt-0.5 size-4 shrink-0 text-accent-strong" aria-hidden /> {ENTITLEMENT_LABEL[e] ?? e}</li>
            ))}
          </ul>
        </div>
      )}
      {error && <p className="text-sm font-medium text-bad-ink">{error}</p>}
      <div className="mt-auto pt-2">
        {free ? (
          <p className="text-sm text-muted">Free tier. No payment needed.</p>
        ) : (
          <Button block loading={busy} onClick={pay}>
            <CreditCard className="size-4" aria-hidden /> {isCurrent ? 'Renew / extend' : 'Pay securely'}
          </Button>
        )}
      </div>
    </Card>
  );
}

/** Shown when the browser returns from PesePay (?ref=). Polls until the payment resolves. */
function PaymentResult({ reference, onResolved, onDismiss }: { reference: string; onResolved: () => void; onDismiss: () => void }) {
  const [status, setStatus] = useState<'PENDING' | 'SUCCESS' | 'FAILED'>('PENDING');
  const [message, setMessage] = useState<string | null>(null);
  const tries = useRef(0);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    async function check() {
      try {
        const r = await getSubscriptionPaymentStatus(reference);
        if (!active) return;
        setStatus(r.status);
        setMessage(r.message);
        if (r.status === 'SUCCESS') {
          onResolved();
          return;
        }
        if (r.status === 'PENDING' && tries.current < 6) {
          tries.current += 1;
          timer = setTimeout(check, 2500);
        }
      } catch {
        if (active && tries.current < 6) {
          tries.current += 1;
          timer = setTimeout(check, 2500);
        }
      }
    }
    check();
    return () => {
      active = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  const tone =
    status === 'SUCCESS' ? 'bg-ok-soft text-ok-ink' : status === 'FAILED' ? 'bg-bad-soft text-bad-ink' : 'bg-surface text-ink-soft';

  return (
    <div className={cn('mb-5 flex items-center gap-3 rounded-card px-4 py-3', tone)}>
      {status === 'PENDING' ? <Loader2 className="size-5 shrink-0 animate-spin" aria-hidden /> : status === 'SUCCESS' ? <Check className="size-5 shrink-0" aria-hidden /> : null}
      <p className="flex-1 text-sm font-medium">
        {status === 'SUCCESS' && 'Payment confirmed. Your subscription is active — an invoice is on its way.'}
        {status === 'PENDING' && 'Confirming your payment…'}
        {status === 'FAILED' && (message || 'That payment didn’t go through. You can try again below.')}
      </p>
      <button type="button" onClick={onDismiss} className="text-sm font-medium underline-offset-2 hover:underline">Dismiss</button>
    </div>
  );
}
